import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL + '/api';

const RegistrationForm = () => {
  const [formData, setFormData] = useState({
    organization_name: '',
    account_name: '', // Organization account name/slug
    industry: '',
    team_size: '', // Required: Team size field
    official_email: '',
    location: '',
    phone_number: '',
    website: '', // Website field
    card_requirement: '',
    contact_person_name: '',
    contact_person_title: '',
    contact_person_phone: '', // Contact person phone
    contact_person_email: '', // Contact person email
    expected_monthly_reviews: '',
    preferred_language: '',
    company_registration_number: '',
    special_requirements: '',
    terms_accepted: false,
    data_protection_accepted: false,
    selected_package_id: '', // Selected subscription package
    selected_billing_cycle: 'monthly' // Billing cycle
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errors, setErrors] = useState({});
  const [packages, setPackages] = useState([]); // Available subscription packages
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [constraintWarning, setConstraintWarning] = useState(null); // For package-team size constraint warnings

  // Helper function to get team size range text for a package
  const getTeamSizeRangeForPackage = (packageObj) => {
    if (!packageObj) return '';
    
    const staffLimit = packageObj.staff_limit;
    
    if (staffLimit === 1) return '1 employee';
    if (staffLimit <= 10) return '2-10 employees';
    if (staffLimit <= 50) return '11-50 employees'; 
    if (staffLimit <= 200) return '51-200 employees';
    return '200+ employees';
  };

  const industryOptions = [
    { value: 'hospital', label: 'Hospital' },
    { value: 'restaurant', label: 'Restaurant' },
    { value: 'retail', label: 'Retail' },
    { value: 'hotel', label: 'Hotel' },
    { value: 'clinic', label: 'Clinic' },
    { value: 'gym', label: 'Gym/Fitness' },
    { value: 'beauty_salon', label: 'Beauty Salon' },
    { value: 'automotive', label: 'Automotive' },
    { value: 'education', label: 'Education' },
    { value: 'real_estate', label: 'Real Estate' },
    { value: 'government', label: 'Government' },
    { value: 'other', label: 'Other' }
  ];

  const teamSizeOptions = [
    { value: '1', label: '1 employee (Self-employed)' },
    { value: '2-5', label: '2-5 employees' },
    { value: '6-10', label: '6-10 employees' },
    { value: '11-25', label: '11-25 employees' },
    { value: '26-50', label: '26-50 employees' },
    { value: '51-100', label: '51-100 employees' },
    { value: '101-200', label: '101-200 employees' },
    { value: '200+', label: '200+ employees' }
  ];

  const locationOptions = [
    { value: 'dubai', label: 'Dubai' },
    { value: 'abu_dhabi', label: 'Abu Dhabi' },
    { value: 'sharjah', label: 'Sharjah' },
    { value: 'ajman', label: 'Ajman' },
    { value: 'uaq', label: 'Umm Al-Quwain' },
    { value: 'rak', label: 'Ras Al Khaimah' },
    { value: 'fujairah', label: 'Fujairah' }
  ];

  const cardRequirementOptions = [
    { value: 'none', label: 'None Required' },
    { value: 'standard_qr', label: 'Standard QR Code Cards' },
    { value: 'nfc_qr', label: 'NFC + QR Code Cards (Premium)' }
  ];

  const monthlyReviewOptions = [
    { value: 'low', label: 'Low (Less than 100 reviews)' },
    { value: 'medium', label: 'Medium (100-500 reviews)' },
    { value: 'high', label: 'High (500-1000 reviews)' },
    { value: 'enterprise', label: 'Enterprise (1000+ reviews)' }
  ];

  const languageOptions = [
    { value: 'english', label: 'English' },
    { value: 'arabic', label: 'Arabic' },
    { value: 'both', label: 'Both English & Arabic' }
  ];

  // Fetch subscription packages on component mount
  useEffect(() => {
    fetchSubscriptionPackages();
  }, []);

  const fetchSubscriptionPackages = async () => {
    try {
      setLoadingPackages(true);
      const response = await axios.get(`${API}/subscription-packages`);
      // Filter out any test packages
      const filteredPackages = response.data.filter(pkg => 
        !pkg.name.toLowerCase().includes('test') && 
        !pkg.name.toLowerCase().includes('premium test')
      );
      setPackages(filteredPackages);
    } catch (error) {
      console.error('Error fetching subscription packages:', error);
      setPackages([]);
    } finally {
      setLoadingPackages(false);
    }
  };

  // Subdomain validation functions - REMOVED (subdomain feature removed)

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Special handling for account_name field
    if (name === 'account_name') {
      const sanitizedValue = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: sanitizedValue
      }));
      return;
    }

    // Special handling for package selection
    if (name === 'selected_package_id') {
      const selectedPackage = packages.find(pkg => pkg.id === value);
      if (selectedPackage) {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
        
        // Clear any previous warnings and errors
        setConstraintWarning(null);
        if (errors.selected_package_id) {
          setErrors(prev => ({ ...prev, selected_package_id: null }));
        }
        return;
      }
    }
    
    // Regular handling for other inputs
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear specific field errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
    
    // Clear constraint warning when user changes other fields
    if (constraintWarning) {
      setConstraintWarning(null);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validation
    const requiredFields = [
      'organization_name', 'account_name', 'industry', 'team_size', 'official_email',
      'location', 'phone_number', 'website', 'card_requirement', 'contact_person_name',
      'contact_person_title', 'contact_person_phone', 'contact_person_email', 'preferred_language', 'selected_package_id'
    ];

    requiredFields.forEach(field => {
      if (!formData[field]) {
        newErrors[field] = 'This field is required';
      }
    });

    // Package selection validation
    if (!formData.selected_package_id) {
      newErrors.selected_package_id = 'Please select a subscription package';
    }

    // Account name validation (basic format check)
    if (formData.account_name) {
      const accountName = formData.account_name;
      if (accountName.length < 3 || accountName.length > 20) {
        newErrors.account_name = 'Account name must be between 3-20 characters';
      } else if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(accountName)) {
        newErrors.account_name = 'Account name must be lowercase letters, numbers, and hyphens only. Cannot start or end with hyphen.';
      }
    }

    // Email validation
    if (formData.official_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.official_email)) {
      newErrors.official_email = 'Please enter a valid email address';
    }

    // Phone validation
    if (formData.phone_number && !/^[\+]?[0-9\s\-\(\)]+$/.test(formData.phone_number)) {
      newErrors.phone_number = 'Please enter a valid phone number';
    }

    // Website validation
    if (formData.website && !/^https?:\/\/.+\..+/.test(formData.website)) {
      newErrors.website = 'Please enter a valid website URL (e.g., https://yourcompany.com)';
    }

    // Terms acceptance validation
    if (!formData.terms_accepted) {
      newErrors.terms_accepted = 'You must accept the terms and conditions';
    }

    if (!formData.data_protection_accepted) {
      newErrors.data_protection_accepted = 'You must accept the data protection agreement';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      console.log('Form submission started');
      
      if (!validateForm()) {
        console.log('Form validation failed');
        return;
      }

      setIsSubmitting(true);
      setSubmitStatus(null);

      try {
        // Debug: Log form data being submitted  
        console.log('Submitting registration request with data:', formData);
        
        const response = await axios.post(`${API}/registration-requests`, formData);
        
        console.log('Registration request successful:', response.data);
        
        setSubmitStatus({
          type: 'success',
          message: 'Registration request submitted successfully! We will review your application and contact you within 2-3 business days.'
        });

        // Reset form
        setFormData({
          organization_name: '',
          account_name: '',
          industry: '',
          team_size: '',
          official_email: '',
          location: '',
          phone_number: '',
          website: '',
          card_requirement: '',
          contact_person_name: '',
          contact_person_title: '',
          contact_person_phone: '',
          contact_person_email: '',
          expected_monthly_reviews: '',
          preferred_language: '',
          company_registration_number: '',
          special_requirements: '',
          terms_accepted: false,
          data_protection_accepted: false,
          selected_package_id: '',
          selected_billing_cycle: 'monthly'
        });
        // Form reset complete

      } catch (error) {
        console.error('Registration submission error:', error);
        
        // Enhanced error handling to prevent blank page
        let errorMessage = 'Failed to submit registration request. Please try again.';
        
        if (error.response?.data?.detail) {
          // Handle Pydantic validation errors
          if (Array.isArray(error.response.data.detail)) {
            const missingFields = error.response.data.detail
              .filter(err => err.type === 'missing')
              .map(err => err.loc[err.loc.length - 1])
              .join(', ');
            errorMessage = `Please fill in all required fields: ${missingFields}`;
          } else {
            errorMessage = error.response.data.detail;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        setSubmitStatus({
          type: 'error',
          message: errorMessage
        });
      } finally {
        setIsSubmitting(false);
      }
    } catch (outerError) {
      console.error('Outer error in form submission:', outerError);
      setIsSubmitting(false);
      setSubmitStatus({
        type: 'error',
        message: 'An unexpected error occurred. Please refresh the page and try again.'
      });
    }
  };

  return (
    <div data-testid="register-page" className="bg-white">
      <section className="py-16 lg:py-20 bg-gradient-to-br from-slate-50 via-white to-blue-50/40">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="mb-10">
            <span className="text-xs uppercase tracking-[0.25em] font-semibold text-blue-600">Get started</span>
            <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              Register your organization.
            </h1>
            <p className="mt-4 text-lg text-slate-600 max-w-2xl">
              Tell us about your business and choose a plan. Your 14-day free trial starts as soon as you're verified.
            </p>
          </div>

          <div className="bg-white rounded-3xl border border-slate-200 shadow-xl shadow-blue-100/40 p-8 lg:p-10">

          {submitStatus && (
            <div className={`mb-6 p-4 rounded-md ${
              submitStatus.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}>
              {submitStatus.message}
            </div>
          )}

          {constraintWarning && (
            <div className={`mb-6 p-4 rounded-md ${
              constraintWarning.type === 'error' ? 'bg-red-50 text-red-800' : 'bg-yellow-50 text-yellow-800'
            }`}>
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {constraintWarning.type === 'error' ? (
                    <span className="text-red-400">⚠️</span>
                  ) : (
                    <span className="text-yellow-400">ℹ️</span>
                  )}
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium">
                    {constraintWarning.type === 'error' ? 'Constraint Error' : 'Package/Team Size Updated'}
                  </p>
                  <p className="text-sm mt-1">{constraintWarning.message}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Organization Information */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4 text-left">Organization Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Organization Name *
                  </label>
                  <input
                    type="text"
                    name="organization_name"
                    value={formData.organization_name}
                    onChange={handleInputChange}
                    className={`w-full border rounded-md px-3 py-2 ${
                      errors.organization_name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Your Company Name"
                  />
                  {errors.organization_name && (
                    <p className="text-red-500 text-sm mt-1">{errors.organization_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Account Name (Unique) *
                  </label>
                  <input
                    type="text"
                    name="account_name"
                    value={formData.account_name}
                    onChange={handleInputChange}
                    className={`w-full border rounded-md px-3 py-2 ${
                      errors.account_name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="yourcompany"
                    maxLength="20"
                  />
                  {errors.account_name && (
                    <p className="text-red-500 text-sm mt-1">{errors.account_name}</p>
                  )}
                  <p className="text-gray-500 text-sm mt-1 text-left">
                    Choose your unique account name (3-20 characters, lowercase letters, numbers, and hyphens only)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Industry *
                  </label>
                  <select
                    name="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                    className={`w-full border rounded-md px-3 py-2 ${
                      errors.industry ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select Industry</option>
                    {industryOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.industry && (
                    <p className="text-red-500 text-sm mt-1">{errors.industry}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Team Size *
                  </label>
                  <select
                    name="team_size"
                    value={formData.team_size}
                    onChange={handleInputChange}
                    className={`w-full border rounded-md px-3 py-2 ${
                      errors.team_size ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                  >
                    <option value="">Select Team Size</option>
                    {teamSizeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.team_size && (
                    <p className="text-red-500 text-sm mt-1">{errors.team_size}</p>
                  )}
                  <p className="text-gray-500 text-sm mt-1 text-left">
                    Select your current team size to help us recommend the right package
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Subscription Package *
                  </label>
                  {loadingPackages ? (
                    <div className="flex justify-center py-4">
                      <div className="text-gray-500">Loading packages...</div>
                    </div>
                  ) : (
                    <div>
                      <select
                        name="selected_package_id"
                        value={formData.selected_package_id}
                        onChange={handleInputChange}
                        className={`w-full border rounded-md px-3 py-2 ${
                          errors.selected_package_id ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                      >
                        <option value="">Select a package</option>
                        {packages.map((pkg) => (
                          <option key={pkg.id} value={pkg.id}>
                            {pkg.name} - {getTeamSizeRangeForPackage(pkg)} (Up to {pkg.staff_limit === 999999 ? 'Unlimited' : pkg.staff_limit} employees)
                          </option>
                        ))}
                      </select>
                      
                      {errors.selected_package_id && (
                        <p className="text-red-500 text-sm mt-1">{errors.selected_package_id}</p>
                      )}
                      
                      <p className="text-gray-500 text-sm mt-1 text-left">
                        Choose the package that matches your team size and needs
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Official Email *
                  </label>
                  <input
                    type="email"
                    name="official_email"
                    value={formData.official_email}
                    onChange={handleInputChange}
                    className={`w-full border rounded-md px-3 py-2 ${
                      errors.official_email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="info@yourcompany.com"
                  />
                  {errors.official_email && (
                    <p className="text-red-500 text-sm mt-1">{errors.official_email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Location *
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className={`w-full border rounded-md px-3 py-2 ${
                      errors.location ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Dubai, UAE"
                  />
                  {errors.location && (
                    <p className="text-red-500 text-sm mt-1">{errors.location}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Preferred Language *
                  </label>
                  <select
                    name="preferred_language"
                    value={formData.preferred_language}
                    onChange={handleInputChange}
                    className={`w-full border rounded-md px-3 py-2 ${
                      errors.preferred_language ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select Language</option>
                    {languageOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.preferred_language && (
                    <p className="text-red-500 text-sm mt-1">{errors.preferred_language}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    className={`w-full border rounded-md px-3 py-2 ${
                      errors.phone_number ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="+971-4-123-4567"
                  />
                  {errors.phone_number && (
                    <p className="text-red-500 text-sm mt-1">{errors.phone_number}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Website *
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    className={`w-full border rounded-md px-3 py-2 ${
                      errors.website ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="https://yourcompany.com"
                  />
                  {errors.website && (
                    <p className="text-red-500 text-sm mt-1">{errors.website}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Card Requirement *
                  </label>
                  <select
                    name="card_requirement"
                    value={formData.card_requirement}
                    onChange={handleInputChange}
                    className={`w-full border rounded-md px-3 py-2 ${
                      errors.card_requirement ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select Card Type</option>
                    {cardRequirementOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.card_requirement && (
                    <p className="text-red-500 text-sm mt-1">{errors.card_requirement}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Person Information */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4 text-left">Contact Person Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Contact Person Name *
                  </label>
                  <input
                    type="text"
                    name="contact_person_name"
                    value={formData.contact_person_name}
                    onChange={handleInputChange}
                    className={`w-full border rounded-md px-3 py-2 ${
                      errors.contact_person_name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="John Smith"
                  />
                  {errors.contact_person_name && (
                    <p className="text-red-500 text-sm mt-1">{errors.contact_person_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Contact Person Title *
                  </label>
                  <input
                    type="text"
                    name="contact_person_title"
                    value={formData.contact_person_title}
                    onChange={handleInputChange}
                    className={`w-full border rounded-md px-3 py-2 ${
                      errors.contact_person_title ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Manager / CEO / Director"
                  />
                  {errors.contact_person_title && (
                    <p className="text-red-500 text-sm mt-1">{errors.contact_person_title}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Contact Person Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="contact_person_phone"
                    value={formData.contact_person_phone}
                    onChange={handleInputChange}
                    className={`w-full border rounded-md px-3 py-2 ${
                      errors.contact_person_phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="+971-50-123-4567"
                  />
                  {errors.contact_person_phone && (
                    <p className="text-red-500 text-sm mt-1">{errors.contact_person_phone}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Contact Person Email *
                  </label>
                  <input
                    type="email"
                    name="contact_person_email"
                    value={formData.contact_person_email}
                    onChange={handleInputChange}
                    className={`w-full border rounded-md px-3 py-2 ${
                      errors.contact_person_email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="john.smith@yourcompany.com"
                  />
                  {errors.contact_person_email && (
                    <p className="text-red-500 text-sm mt-1">{errors.contact_person_email}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Additional Information (Optional) */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4 text-left">Additional Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Company Registration Number
                  </label>
                  <input
                    type="text"
                    name="company_registration_number"
                    value={formData.company_registration_number}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Trade License Number"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    Special Requirements
                  </label>
                  <textarea
                    name="special_requirements"
                    value={formData.special_requirements}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Any special requirements or additional information..."
                  />
                </div>
              </div>
            </div>

            {/* Terms and Conditions */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4 text-left">Terms and Conditions</h2>
              <div className="space-y-4">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    name="terms_accepted"
                    checked={formData.terms_accepted}
                    onChange={handleInputChange}
                    className="mt-1 mr-3"
                  />
                  <span className="text-sm text-gray-700">
                    I accept the <a href="#" className="text-blue-600 hover:underline">Terms and Conditions</a> *
                  </span>
                </label>
                {errors.terms_accepted && (
                  <p className="text-red-500 text-sm">{errors.terms_accepted}</p>
                )}

                <label className="flex items-start">
                  <input
                    type="checkbox"
                    name="data_protection_accepted"
                    checked={formData.data_protection_accepted}
                    onChange={handleInputChange}
                    className="mt-1 mr-3"
                  />
                  <span className="text-sm text-gray-700">
                    I accept the <a href="#" className="text-blue-600 hover:underline">Data Protection Agreement</a> and understand how my data will be processed *
                  </span>
                </label>
                {errors.data_protection_accepted && (
                  <p className="text-red-500 text-sm">{errors.data_protection_accepted}</p>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="text-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-8 py-3 rounded-md text-white font-medium ${
                  isSubmitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Registration Request'}
              </button>
              
              <p className="text-sm text-gray-500 mt-4">
                After submission, we will review your application and contact you within 2-3 business days.
              </p>
            </div>
          </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RegistrationForm;